import { PDFDocument } from 'pdf-lib';

/**
 * Checks if a PDF file should be split based on a size threshold
 * @param file The PDF file to check
 * @param threshold The size threshold in bytes
 * @returns True if the file exceeds the threshold and should be split
 */
export const shouldSplitPDF = (file: File, threshold: number = 10 * 1024 * 1024): boolean => {
  return true;
};

interface SplitPDFResult {
  chunks: Uint8Array[];
  totalPages: number;
  chunkSizes: number[];
  pageRanges: Array<{ start: number; end: number }>;
}

/**
 * Splits a PDF file into multiple smaller chunks
 * @param file The PDF file to split
 * @param maxPagesPerChunk Maximum pages per chunk (default: 10)
 * @returns Promise resolving to an object containing the split chunks and metadata
 */
export const splitPDF = async (
  file: File,
  maxPagesPerChunk: number = 1
): Promise<SplitPDFResult> => {
  // Convert File to ArrayBuffer
  const fileBuffer = await file.arrayBuffer();

  // Load the source PDF document
  const srcDoc = await PDFDocument.load(fileBuffer);
  const pageCount = srcDoc.getPageCount();

  // Initialize result
  const result: SplitPDFResult = {
    chunks: [],
    totalPages: pageCount,
    chunkSizes: [],
    pageRanges: [],
  };

  // If document is small enough, return it as is
  if (pageCount <= maxPagesPerChunk) {
    const pdfBytes = await srcDoc.save();
    result.chunks.push(pdfBytes);
    result.chunkSizes.push(pdfBytes.byteLength);
    result.pageRanges.push({ start: 1, end: pageCount });
    return result;
  }

  // Calculate number of chunks needed
  const numChunks = Math.ceil(pageCount / maxPagesPerChunk);

  // Split document into chunks
  for (let chunkIndex = 0; chunkIndex < numChunks; chunkIndex++) {
    // Calculate page range for this chunk
    const startPage = chunkIndex * maxPagesPerChunk;
    const endPage = Math.min((chunkIndex + 1) * maxPagesPerChunk - 1, pageCount - 1);
    const pageIndices = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    // Create a new document for this chunk
    const chunkDoc = await PDFDocument.create();

    // Copy the pages from source to this chunk
    const copiedPages = await chunkDoc.copyPages(srcDoc, pageIndices);
    copiedPages.forEach(page => chunkDoc.addPage(page));

    // Save the chunk
    const chunkBytes = await chunkDoc.save();

    // Add to result
    result.chunks.push(chunkBytes);
    result.chunkSizes.push(chunkBytes.byteLength);
    result.pageRanges.push({
      start: startPage + 1, // Convert to 1-based indexing for better readability
      end: endPage + 1,
    });
  }

  return result;
};

/**
 * Converts a split PDF result to File objects
 * @param result The result from splitPDF
 * @param originalFilename The original filename
 * @returns Array of File objects
 */
export const chunksToFiles = (result: SplitPDFResult, originalFilename: string): File[] => {
  return result.chunks.map((chunk, index) => {
    const nameWithoutExt = originalFilename.replace(/\.pdf$/i, '');
    const newName =
      result.chunks.length > 1
        ? `${nameWithoutExt}-Page-${index + 1}.pdf`
        : `${nameWithoutExt}.pdf`;

    return new File([chunk], newName, { type: 'application/pdf' });
  });
};
